import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

export default async function fetchEducationalAttainmentByRaceSupabase(location_name) {
  try {
    
    // Initialize Supabase location service to get county information
    await supabaseLocationService.initialize();
    
    // Check if this is a city (no county comparison needed)
    const locationInfo = supabaseLocationService.getAvailableLocations().find(loc => loc.name === location_name);
    const isCity = locationInfo && locationInfo.type === 'city';
    

    // Build targeted Supabase query - only download what we need
    
    // Build separate queries for each location (like the working HTML version)
    let localQuery = supabaseClient
      .from('educational_attain_race')
      .select('*')
      .eq('location_name', location_name)
      .eq('educ_level', "Bachelor\\'s degree or higher")
      .gte('year', 2010)
      .lte('year', 2023)
      .order('year', { ascending: true });
    
    // Add California query for comparison
    const californiaQuery = supabaseClient
      .from('educational_attain_race')
      .select('*')
      .eq('location_name', 'California')
      .eq('educ_level', "Bachelor\\'s degree or higher")
      .gte('year', 2010)
      .lte('year', 2023)
      .order('year', { ascending: true });
    
    // Execute queries
    const { data: localQueryData, error: localError } = await localQuery;
    const { data: californiaQueryData, error: californiaError } = await californiaQuery;
    
    if (localError) throw localError;
    if (californiaError) console.warn('Error fetching California data:', californiaError);
    
    // Combine local and California data
    const data = [...(localQueryData || []), ...(californiaQueryData || [])];
    
    
    // If no data found, try a broader query to see what's available
    if (data.length === 0) {
      
      try {
        const { data: broaderData, error: broaderError } = await supabaseClient
          .from('educational_attain_race')
          .select('*')
          .limit(10);
        
        if (!broaderError && broaderData && broaderData.length > 0) {
          
          // Check what values exist for key columns
          const sampleLocations = [...new Set(broaderData.map(row => row.location_name))];
          const sampleEducLevels = [...new Set(broaderData.map(row => row.educ_level))];
          const sampleGenders = [...new Set(broaderData.map(row => row.gender))];
          
        }
      } catch (broaderError) {
      }
    }
    
    // Show unique location names in the data
    const uniqueLocations = [...new Set(data.map(row => row.location_name))];
    
    // Debug: Show what we found
    
    // Show the actual filtered data structure
    if (data.length > 0) {
      data.slice(0, 3).forEach((row, index) => {
        console.log('Sample data row:', {
          location: row.location_name,
          year: row.year,
          race: row.race,
          gender: row.gender,
          educ_level: row.educ_level,
          percent: row.percent,
          allColumns: Object.keys(row)
        });
      });
    }
    
    // Show unique race values
    const uniqueRaces = [...new Set(data.map(row => row.race))];
    
    // Show unique educational level values
    const uniqueEducLevels = [...new Set(data.map(row => row.educ_level))];
    
    // Show unique gender values
    const uniqueGenders = [...new Set(data.map(row => row.gender))];
    
    // Check numeric columns
    const numericColumns = Object.keys(data[0] || {}).filter(col => {
      const sampleValue = data[0][col];
      return typeof sampleValue === 'number' || (typeof sampleValue === 'string' && !isNaN(parseFloat(sampleValue)));
    });
    
    // Check ALL columns to see what's available
    if (data.length > 0) {
      
      // Check if we have the expected structure
      const hasEducLevel = Object.keys(data[0]).includes('educ_level');
      const hasRace = Object.keys(data[0]).includes('race');
      const hasPercent = Object.keys(data[0]).includes('percent');
      const hasYear = Object.keys(data[0]).includes('year');
      
    }
    
    // Show complete sample rows

    // Separate local and California data
    const localData = data.filter(d => 
      d.location_name && 
      d.location_name.toString().toLowerCase().includes(location_name.toLowerCase())
    );
    const californiaData = data.filter(d => 
      d.location_name && 
      d.location_name.toString().toLowerCase().includes('california')
    );
    
    // Debug: Show what we found
    console.log('Local data found:', localData.map(d => ({
      location: d.location_name, 
      race: d.race, 
      gender: d.gender,
      year: d.year 
    })));
    
    console.log('Data summary:', {
      localData: localData.length,
      californiaData: californiaData.length,
      localDataSample: localData[0],
      californiaDataSample: californiaData[0]
    });

    // If no local data, try to find similar data or use available data
    if (localData.length === 0) {
      
      // Look for any city data that might be representative
      const availableCities = data.filter(row => 
        row.location_name && 
        row.location_name !== 'California' &&
        row.location_name !== location_name &&
        row.gender === 'Total' // Use total gender data
      );
      
      if (availableCities.length > 0) {
        // Use the first available city as a fallback
        const fallbackCity = availableCities[0];
        
        // Create a message that this is representative data
        const message = `Educational attainment data for ${location_name} is not available. Showing representative data from ${fallbackCity.location_name} instead.`;
        
        // Use the fallback city data (already filtered at database level)
        localData.push(...data.filter(row => 
          row.location_name === fallbackCity.location_name &&
          row.educ_level === "Bachelor\\'s degree or higher"
        ));
      } else {
        throw new Error('No educational attainment data available for any cities');
      }
    }
    
    // Get the most recent year available
    const years = [...new Set(data.map(row => row.year))].sort((a, b) => b - a);
    const mostRecentYear = years[0];
    
    // Data is already filtered at database level for Bachelor's degree
    // We need to aggregate Male/Female data to get total values
    const recentLocalData = localData.filter(d => d.year === mostRecentYear);
    const recentCaliforniaData = californiaData.filter(d => d.year === mostRecentYear);
    
    // Aggregate data by race (combine Male/Female records)
    const aggregateData = (data) => {
      const aggregated = {};
      data.forEach(row => {
        const key = `${row.race}_${row.year}`;
        if (!aggregated[key]) {
          aggregated[key] = {
            race: row.race,
            year: row.year,
            percent: 0,
            count: 0,
            records: []
          };
        }
        // Store individual records for debugging
        aggregated[key].records.push(row);
        // Sum the counts but average the percentages
        aggregated[key].count += (row.count || 0);
      });
      
      // Calculate proper percentages - use the "Total" gender record if available, otherwise use Female
      Object.values(aggregated).forEach(raceData => {
        const totalRecord = raceData.records.find(r => r.gender === 'Total');
        if (totalRecord) {
          // Use the Total record's percentage (convert from decimal to percentage)
          raceData.percent = (totalRecord.percent || 0) * 100;
        } else {
          // Use Female record's percentage (convert from decimal to percentage)
          const femaleRecord = raceData.records.find(r => r.gender === 'Female');
          raceData.percent = femaleRecord ? (femaleRecord.percent || 0) * 100 : 0;
        }
      });
      
      return Object.values(aggregated);
    };
    
    const aggregatedLocalData = aggregateData(recentLocalData);
    const aggregatedCaliforniaData = aggregateData(recentCaliforniaData);
    
    console.log('Raw recent local data sample:', recentLocalData.slice(0, 3));
    console.log('Raw recent California data sample:', recentCaliforniaData.slice(0, 3));
    console.log('Aggregated local data sample:', aggregatedLocalData.slice(0, 3));
    console.log('Aggregated California data sample:', aggregatedCaliforniaData.slice(0, 3));
    console.log('Aggregated data summary:', {
      localData: recentLocalData.length,
      californiaData: recentCaliforniaData.length,
      aggregatedLocalData: aggregatedLocalData.length,
      aggregatedCaliforniaData: aggregatedCaliforniaData.length
    });

    // Get unique races for the chart
    const races = [...new Set(aggregatedLocalData.map(row => row.race))].filter(race => race && race !== '');

    // Sort races by local data percentages (highest to lowest)
    const sortedRaces = races.sort((a, b) => {
      const aData = aggregatedLocalData.find(r => r.race === a);
      const bData = aggregatedLocalData.find(r => r.race === b);
      const aPercent = aData?.percent || 0;
      const bPercent = bData?.percent || 0;
      return bPercent - aPercent; // Sort highest to lowest
    });

    // Build datasets for the chart
    const datasets = [];
    
    // Local location data
    if (aggregatedLocalData.length > 0) {
      const localDataset = {
        label: location_name,
        data: sortedRaces.map(race => {
          const row = aggregatedLocalData.find(r => r.race === race);
          return row?.percent || 0;
        }),
        backgroundColor: '#3b82f6',
        borderColor: '#1e40af',
        borderWidth: 1
      };
      datasets.push(localDataset);
    }
    
    // California data for comparison
    if (aggregatedCaliforniaData.length > 0) {
      const californiaDataset = {
        label: 'California',
        data: sortedRaces.map(race => {
          const row = aggregatedCaliforniaData.find(r => r.race === race);
          return row?.percent || 0;
        }),
        backgroundColor: '#10b981',
        borderColor: '#059669',
        borderWidth: 1
      };
      datasets.push(californiaDataset);
    }

    
    return {
      chartData: {
        labels: sortedRaces,
        datasets: datasets
      },
      chartOptions: {
        type: 'bar',
        indexAxis: 'y', // This makes it a horizontal bar chart
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          title: {
            display: true,
            text: (() => {
              // Check if we're using fallback data
              const isUsingFallback = localData.some(row => 
                row.location_name !== location_name
              );
              
              if (isUsingFallback) {
                const fallbackCity = localData.find(row => 
                  row.location_name !== location_name
                )?.location_name || 'Representative City';
                return `Educational Attainment by Race - ${location_name} (Data from ${fallbackCity}) vs California (${mostRecentYear})`;
              } else {
                return `Educational Attainment by Race - ${location_name} vs California (${mostRecentYear})`;
              }
            })()
          },
          tooltip: {
            callbacks: {
              label: context => `${context.dataset.label}: ${typeof context.parsed.x === 'number' ? context.parsed.x.toFixed(1) : 'N/A'}%`
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: '% with Bachelor\'s Degree or Higher'
            },
            ticks: {
              callback: value => `${Number(value).toFixed(1)}%`
            },
            beginAtZero: true
          },
          y: {
            title: {
              display: true,
              text: 'Race/Ethnicity'
            }
          }
        }
      }
    };

  } catch (err) {
    console.error('Failed to generate educational attainment chart:', err);
    throw err;
  }
}
